<template>
  <div class="travel-guide">
    <!-- 封面部分 -->
    <div class="cover-section">
      <img :src="guide.coverImage" :alt="guide.title" class="cover-image">
      <div class="cover-content">
        <h1 class="title">{{ guide.title }}</h1>
        <p class="subtitle">{{ guide.subtitle }}</p>
      </div>
    </div>

    <!-- 基本信息部分 -->
    <div class="info-section">
      <div class="info-card">
        <div class="info-icon">
          <!-- 简单的日历图标 -->
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
        </div>
        <div class="info-content">
          <span class="info-label">行程天数</span>
          <span class="info-value">{{ guide.duration }}</span>
        </div>
      </div>
      
      <div class="info-card">
        <div class="info-icon">
          <!-- 简单的钱币图标 -->
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="16"></line>
            <line x1="8" y1="12" x2="16" y2="12"></line>
          </svg>
        </div>
        <div class="info-content">
          <span class="info-label">人均花费</span>
          <span class="info-value">{{ guide.budget }}</span>
        </div>
      </div>
      
      <div class="info-card">
        <div class="info-icon">
          <!-- 简单的时钟图标 -->
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
        </div>
        <div class="info-content">
          <span class="info-label">最佳时间</span>
          <span class="info-value">{{ guide.bestTime }}</span>
        </div>
      </div>
      
      <div class="info-card">
        <div class="info-icon">
          <!-- 简单的人物图标 -->
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        </div>
        <div class="info-content">
          <span class="info-label">适合人群</span>
          <span class="info-value">{{ guide.idealFor }}</span>
        </div>
      </div>
    </div>

    <!-- 主要内容 -->
    <div class="main-content">
      <!-- 简介 -->
      <section class="section">
        <h2 class="section-title">攻略简介</h2>
        <div class="introduction" v-html="guide.introduction"></div>
      </section>

      <!-- 行程亮点 -->
      <section class="section">
        <h2 class="section-title">行程亮点</h2>
        <ul class="highlights-list">
          <li v-for="(highlight, index) in guide.highlights" :key="index" class="highlight-item">
            <span class="highlight-number">{{ index + 1 }}</span>
            <span>{{ highlight }}</span>
          </li>
        </ul>
      </section>

      <!-- 行程安排 -->
      <section class="section">
        <h2 class="section-title">行程安排</h2>
        
        <div class="day-tabs">
          <button 
            v-for="(day, index) in guide.itinerary" 
            :key="index"
            class="day-tab"
            :class="{ 'active': selectedDay === index }"
            @click="selectedDay = index"
          >
            第{{ index + 1 }}天
          </button>
        </div>
        
        <div class="day-content" v-for="(day, index) in guide.itinerary" :key="index" v-show="selectedDay === index">
          <h3 class="day-title">{{ day.title }}</h3>
          <p class="day-description">{{ day.description }}</p>
          
          <div class="activities">
            <div class="activity" v-for="(activity, actIndex) in day.activities" :key="actIndex">
              <div class="activity-time" v-if="activity.time">{{ activity.time }}</div>
              <div class="activity-content">
                <h4 class="activity-title">{{ activity.title }}</h4>
                <p class="activity-description">{{ activity.description }}</p>
                <div class="activity-location" v-if="activity.location">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <span>{{ activity.location }}</span>
                </div>
                <img v-if="activity.image" :src="activity.image" :alt="activity.title" class="activity-image">
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 旅行贴士 -->
      <section class="section">
        <h2 class="section-title">旅行贴士</h2>
        <div class="tips-container">
          <div class="tip" v-for="(tip, index) in guide.tips" :key="index">
            <h3 class="tip-title">{{ tip.title }}</h3>
            <p class="tip-content">{{ tip.content }}</p>
          </div>
        </div>
      </section>

      <!-- 美食推荐 -->
      <section class="section" v-if="guide.foodRecommendations && guide.foodRecommendations.length">
        <h2 class="section-title">美食推荐</h2>
        <div class="food-container">
          <div class="food-item" v-for="(food, index) in guide.foodRecommendations" :key="index">
            <img v-if="food.image" :src="food.image" :alt="food.name" class="food-image">
            <div class="food-content">
              <h3 class="food-name">{{ food.name }}</h3>
              <p class="food-description">{{ food.description }}</p>
              <div class="food-location" v-if="food.location">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <span>{{ food.location }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 图片画廊 -->
      <section class="section" v-if="guide.gallery && guide.gallery.length">
        <h2 class="section-title">旅行图集</h2>
        <div class="gallery">
          <div class="gallery-item" v-for="(image, index) in guide.gallery" :key="index">
            <img :src="image" :alt="`旅行图片 ${index + 1}`" class="gallery-image">
          </div>
        </div>
      </section>

      <!-- 作者信息 -->
      <section class="section author-section">
        <div class="author-info">
          <img :src="guide.author.avatar" :alt="guide.author.name" class="author-avatar">
          <div class="author-content">
            <h3 class="author-name">{{ guide.author.name }}</h3>
            <p class="author-bio">{{ guide.author.bio }}</p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'

// 定义响应式状态
const selectedDay = ref(0)

// 获取路由参数
const route = useRoute()

// 旅游攻略数据
const guideData = [
  {
    "name": "wutai",
    "title": "五台山春季杜鹃花海朝圣之旅",
    "subtitle": "踏访佛教圣地，沉醉高山花海",
    "coverImage": "/images/攻略图片集/五台山攻略/图片1.png",
    "duration": "5天4晚",
    "budget": "约5000元/人",
    "bestTime": "春季 (4月下旬-5月中旬)",
    "idealFor": "自然爱好者、摄影爱好者、佛教信徒",
    "introduction": "五台山，中国四大佛教名山之首，每逢春季杜鹃盛开时节，海拔2000米以上的高山草甸便化作粉色花海。这里既有庄严的佛教古刹，又有壮观的高山花海，是自然与人文完美融合的朝圣之地。本攻略将带您领略佛国花海的独特魅力，从古寺巡礼到高山徒步，感受心灵与自然的双重净化。",
    "highlights": [
      "登临东台顶观赏云海杜鹃奇观",
      "探访显通寺、塔院寺等千年古刹",
      "徒步高山草甸杜鹃花海步道",
      "拍摄晨雾中的古寺与花海同框",
      "体验寺庙素斋与禅修课程"
    ],
    "itinerary": [
      {
        "title": "抵达五台山 & 核心区初探",
        "description": "抵达五台山景区，适应高海拔环境，初探台怀镇核心寺庙群",
        "activities": [
          {
            "time": "12:00",
            "title": "抵达五台山景区",
            "description": "从太原乘坐景区直通车进入五台山",
            "location": "五台山游客中心"
          },
          {
            "time": "15:00",
            "title": "显通寺朝拜",
            "description": "参观五台山开山祖寺，欣赏铜殿建筑奇观",
            "image": "/images/攻略图片集/五台山攻略/通显寺.png",
            "location": "显通寺"
          },
          {
            "time": "18:00",
            "title": "素斋体验",
            "description": "在寺庙体验传统佛教素斋",
            "location": "塔院寺斋堂"
          }
        ]
      },
      {
        "title": "台怀镇核心景区深度游",
        "description": "全天游览核心区重点寺庙，傍晚前往观景台拍摄花海日落",
        "activities": [
          {
            "time": "08:00",
            "title": "五爷庙祈福",
            "description": "朝拜香火最旺的万佛阁",
            "location": "五爷庙"
          },
          {
            "time": "14:00",
            "title": "黛螺顶朝圣",
            "description": "攀登1080级大智路，俯瞰台怀镇全景",
            "image": "https://example.com/dailuo-summit.jpg",
            "location": "黛螺顶"
          }
        ]
      },
      {
        "title": "东台顶高山花海徒步",
        "description": "全天高山徒步，穿越杜鹃花海，探访古台顶寺庙",
        "activities": [
          {
            "time": "06:00",
            "title": "东台望海峰观日出",
            "description": "乘车前往东台顶，观赏云海日出与杜鹃花海",
            "image": "https://example.com/east-peak-sunrise.jpg",
            "location": "东台顶"
          },
          {
            "time": "10:00",
            "title": "高山草甸徒步",
            "description": "沿花海步道徒步至澡浴池寺",
            "location": "中台翠岩峰"
          }
        ]
      },
      {
        "title": "古南台探秘 & 民俗体验",
        "description": "探访幽静的古南台区，体验当地民俗文化",
        "activities": [
          {
            "time": "09:00",
            "title": "佛母洞朝圣",
            "description": "参观天然石灰岩洞奇观",
            "location": "佛母洞"
          },
          {
            "time": "15:00",
            "title": "农家体验",
            "description": "学习制作台蘑酱等当地特色",
            "location": "金岗库村"
          }
        ]
      },
      {
        "title": "返程 & 纪念品采购",
        "description": "清晨最后朝拜，采购佛珠等纪念品后返程",
        "activities": [
          {
            "time": "07:00",
            "title": "菩萨顶早课",
            "description": "参加皇家寺庙的晨间诵经",
            "location": "菩萨顶"
          },
          {
            "time": "10:00",
            "title": "特色市场采购",
            "description": "选购台蘑、佛珠等特产",
            "location": "台怀镇商业街"
          }
        ]
      }
    ],
    "tips": [
      {
        "title": "交通提示",
        "content": "景区内建议购买观光车通票，前往台顶需预约景区专用车辆"
      },
      {
        "title": "着装建议",
        "content": "四月末五台山气温约5-15℃，需准备防风外套和登山鞋"
      },
      {
        "title": "摄影装备",
        "content": "建议携带广角镜头拍摄花海，长焦镜头捕捉远山古寺"
      },
      {
        "title": "礼仪注意",
        "content": "进入寺庙需衣着得体，未经允许不可拍摄佛像"
      }
    ],
    "foodRecommendations": [
      {
        "name": "妙德素斋",
        "description": "百年历史的寺庙素斋，招牌菜罗汉斋和素东坡肉",
        "image": "/images/攻略图片集/五台山攻略/妙德素斋.png",
        "location": "显通寺内"
      },
      {
        "name": "五台山台蘑宴",
        "description": "以当地特产台蘑制作的特色菜肴",
        "image": "/images/攻略图片集/五台山攻略/蘑菇宴.png",
        "location": "金都山庄"
      }
    ],
    "gallery": [
      "/images/攻略图片集/五台山攻略/图片1.png",
      "/images/攻略图片集/五台山攻略/图片2.png",
      "/images/攻略图片集/五台山攻略/图片3.png",
      "/images/攻略图片集/五台山攻略/图片1.png"
    ],
    "author": {
      "name": "禅旅行者",
      "avatar": "https://example.com/zen-traveler-avatar.jpg",
      "bio": "专注佛教圣地深度游，已完成四大佛教名山朝圣之路"
    }
  },
  {
    "name": "pingyao",
    "title": "平遥古城秋韵之旅",
    "subtitle": "穿越千年古建，邂逅金色晋商文化",
    "coverImage": "/images/景点图片文件夹/平遥古城图片集/平遥古城1.jpg",
    "duration": "4天3晚",
    "budget": "约3500元/人",
    "bestTime": "秋季 (10月-11月)",
    "idealFor": "历史爱好者、摄影达人、文化探索者",
    "introduction": "当秋日暖阳洒在明清古建的灰砖青瓦上，平遥古城迎来了一年中最诗意的季节。城墙脚下的银杏树披上金甲，票号旧址的石板路铺满落叶，这座世界文化遗产古城在秋色中焕发出独特的韵味。本攻略将带您漫步古城街巷，探秘晋商文化，感受时光凝固的秋日画卷。",
    "highlights": [
      "登明代城墙俯瞰古城秋色全景",
      "夜游明清街赏灯笼映照古建",
      "在日升昌票号体验晋商银票文化",
      "拍摄县衙古柏与金色银杏同框",
      "品味地道平遥牛肉与黄酒"
    ],
    "itinerary": [
      {
        "title": "初识平遥 & 古城夜韵",
        "description": "抵达古城入住特色客栈，感受夜幕下的明清风情",
        "activities": [
          {
            "time": "15:00",
            "title": "古城入住",
            "description": "入住明清风格四合院客栈，体验炕床文化",
            "location": "古城内客栈",
            "image": "/images/攻略图片集/平遥古城/平遥古城客栈.png"
          },
          {
            "time": "18:30",
            "title": "明清街夜游",
            "description": "灯笼初上时漫步古城中轴线，拍摄夜景",
            "location": "明清街",
            "image": "/images/攻略图片集/平遥古城/平遥古城夜景.png"
          }
        ]
      },
      {
        "title": "古城深度探索",
        "description": "全天探访世界文化遗产核心建筑群",
        "activities": [
          {
            "time": "08:30",
            "title": "古城墙巡礼",
            "description": "登明代城墙观日出，拍摄古城全景",
            "location": "平遥城墙",
            "image": "/images/攻略图片集/平遥攻略/城墙.png"
          },
          {
            "time": "14:00",
            "title": "票号文化体验",
            "description": "参观日升昌票号+协同庆钱庄，体验古代汇兑",
            "location": "日升昌票号",
            "image": "/images/攻略图片集/平遥攻略/票号.png"
          }
        ]
      },
      {
        "title": "晋商文化 & 秋色摄影",
        "description": "捕捉古建筑与秋叶的完美结合",
        "activities": [
          {
            "time": "06:30",
            "title": "县衙晨拍",
            "description": "拍摄古县衙千年侧柏与晨光中的银杏",
            "location": "平遥县衙",
            "image": "/images/攻略图片集/平遥攻略/县衙秋色.png"
          },
          {
            "time": "10:00",
            "title": "双林寺探秘",
            "description": "欣赏彩塑艺术，拍摄寺内古树秋色",
            "location": "双林寺",
            "image": "/images/攻略图片集/平遥攻略/双林寺.png"
          }
        ]
      },
      {
        "title": "民俗体验 & 返程",
        "description": "最后感受古城生活气息，选购特色纪念品",
        "activities": [
          {
            "time": "09:00",
            "title": "推光漆器工坊",
            "description": "学习国家级非遗推光漆器制作工艺",
            "location": "永隆号漆器博物馆"
          },
          {
            "time": "13:00",
            "title": "古城手信采购",
            "description": "选购平遥牛肉、长山药粉等特产",
            "location": "南大街特产店"
          }
        ]
      }
    ],
    "tips": [
      {
        "title": "交通建议",
        "content": "古城内禁止机动车，建议租用环保电瓶车代步"
      },
      {
        "title": "摄影装备",
        "content": "建议携带偏振镜消除古建漆面反光，长焦镜头捕捉建筑细节"
      },
      {
        "title": "着装提示",
        "content": "10月平均气温8-20℃，建议叠穿方便穿脱的汉服或复古装束"
      },
      {
        "title": "文化礼仪",
        "content": "参观民居时不随意触碰门窗木雕，拍摄商铺需先征得同意"
      }
    ],
    "foodRecommendations": [
      {
        "name": "洪武记黄米凉糕",
        "description": "传承三代的非遗小吃，配桂花蜜清香不腻",
        "image": "/images/攻略图片集/平遥古城/黄米凉糕.png",
        "location": "洪武记饭店"
      },
      {
        "name": "董记炒碗托",
        "description": "平遥特色面食，配麻花和秘制辣油",
        "image": "/images/攻略图片集/平遥古城/董记碗托.png",
        "location": "董记炒碗托"
      }
    ],
    "gallery": [
      "/images/景点图片文件夹/平遥古城图片集/平遥古城1.jpg",
      "/images/景点图片文件夹/平遥古城图片集/平遥古城2.jpg",
      "/images/景点图片文件夹/平遥古城图片集/平遥古城3.jpg",
      "/images/景点图片文件夹/平遥古城图片集/平遥古城4.jpg"
    ],
    "author": {
      "name": "晋韵旅人",
      "avatar": "/images/攻略图片集/平遥攻略/作者头像.png",
      "bio": "专注山西古建文化研究，行走过全省73处国保单位"
    }
  },
  {
    "name": "hengshan",
    "title": "恒山悬空寺避暑朝圣之旅",
    "subtitle": "登临北岳之巅，探秘千年悬空奇观",
    "coverImage": "/images/攻略图片集/恒山攻略/图片1.png",
    "duration": "5天4晚",
    "budget": "约6000元/人",
    "bestTime": "夏季 (6月-8月)",
    "idealFor": "登山爱好者、历史文化爱好者、摄影发烧友",
    "introduction": "恒山，五岳之北岳，以「人天北柱」之称闻名于世。夏季平均气温18-25℃，是华北地区著名避暑胜地。这里不仅有惊险奇绝的悬空寺凌空悬挂，更有壮观的恒山十八景等待探索。本攻略将带您穿行于悬崖古寺之间，感受「危楼高百尺，手可摘星辰」的独特体验，领略道教圣地的清凉与玄妙。",
    "highlights": [
      "悬空寺全景深度游览",
      "登顶恒山主峰天峰岭(2016米)",
      "探访果老岭、飞石窟等道教遗迹",
      "拍摄金龙峡日落奇观",
      "体验浑源特色民俗文化"
    ],
    "itinerary": [
      {
        "title": "抵达浑源 & 初探悬空寺",
        "description": "适应山地气候，近距离感受建筑奇迹",
        "activities": [
          {
            "time": "14:00",
            "title": "抵达浑源县城",
            "description": "从大同南站乘坐旅游专线抵达",
            "image" : "/images/攻略图片集/恒山攻略/图片2.png",
            "location": "浑源汽车站"
          },
          {
            "time": "16:00",
            "title": "悬空寺首探",
            "description": "观赏寺庙建筑结构与力学奇迹",
            "image": "/images/攻略图片集/恒山攻略/图片3.png",
            "location": "悬空寺景区"
          }
        ]
      },
      {
        "title": "恒宗主峰登山挑战",
        "description": "征服天峰岭，俯瞰群山壮阔",
        "activities": [
          {
            "time": "07:30",
            "title": "登山起点",
            "description": "从恒山门开始徒步登山",
            "location": "恒山登山步道"
          },
          {
            "time": "12:00",
            "title": "登顶天峰岭",
            "description": "在恒山极顶俯瞰晋北大地",
            "image": "/images/攻略图片集/恒山攻略/天峰岭全景.png",
            "location": "天峰岭"
          }
        ]
      },
      {
        "title": "悬空寺深度探索",
        "description": "多角度感受千年古寺魅力",
        "activities": [
          {
            "time": "05:30",
            "title": "晨光摄影",
            "description": "拍摄悬空寺日出金顶奇观",
            "image": "/images/攻略图片集/恒山攻略/金顶奇观.png",
            "location": "悬空寺观景台"
          },
          {
            "time": "09:00",
            "title": "登寺体验",
            "description": "实地探访悬空寺内部结构",
            "location": "悬空寺"
          }
        ]
      },
      {
        "title": "文化体验日",
        "description": "探索道教文化与当地民俗",
        "activities": [
          {
            "time": "08:30",
            "title": "北岳庙朝拜",
            "description": "参观恒山主庙了解祭祀文化",
            "location": "北岳庙"
          },
          {
            "time": "14:00",
            "title": "浑源古城游",
            "description": "参观永安寺元代壁画",
            "image": "/images/攻略图片集/恒山攻略/图片2.png",
            "location": "浑源古城"
          }
        ]
      },
      {
        "title": "返程 & 特产采购",
        "description": "最后领略峡谷风光，带回北岳记忆",
        "activities": [
          {
            "time": "08:00",
            "title": "金龙峡徒步",
            "description": "穿越恒山天然地质走廊",
            "location": "金龙峡"
          },
          {
            "time": "12:00",
            "title": "特产采购",
            "description": "选购恒山黄芪、正北芪等特产",
            "location": "浑源土特产市场"
          }
        ]
      }
    ],
    "tips": [
      {
        "title": "登山准备",
        "content": "建议携带登山杖，景区部分路段较陡需穿防滑鞋"
      },
      {
        "title": "限流提示",
        "content": "悬空寺每日限流3000人，建议提前3天预约"
      },
      {
        "title": "温差注意",
        "content": "山顶与山下温差达10℃，需备防风外套"
      },
      {
        "title": "拍摄建议",
        "content": "悬空寺最佳全景拍摄点在翠屏峰观景台"
      }
    ],
    "foodRecommendations": [
      {
        "name": "浑源凉粉",
        "description": "当地非遗美食，配秘制辣椒油风味独特",
        "image": "/images/攻略图片集/恒山攻略/浑源凉粉.png",
        "location": "小媳妇凉粉总店"
      },
      {
        "name": "恒山黄芪炖鸡",
        "description": "药膳佳肴，采用五年生北岳黄芪",
        "image": "/images/攻略图片集/恒山攻略/黄芪炖鸡.png",
        "location": "北岳农家院"
      }
    ],
    "gallery": [
      "/images/攻略图片集/恒山攻略/图片1.png",
      "/images/攻略图片集/恒山攻略/图片2.png",
      "/images/攻略图片集/恒山攻略/图片3.png",
      "/images/攻略图片集/恒山攻略/图片4.png"
    ],
    "author": {
      "name": "北岳行者",
      "avatar": "/images/攻略图片集/恒山攻略/作者头像.png",
      "bio": "深耕山西古建旅游10年，认证登山向导"
    }
  },
  {
    "name": "wutai-winter",
    "title": "五台山雪域佛国朝圣之旅",
    "subtitle": "银装素裹中的禅意修行",
    "coverImage": "/images/攻略图片集/五台山雪景/五台山雪景2.png",
    "duration": "5天4晚",
    "budget": "约5500元/人",
    "bestTime": "冬季 (12月-2月)",
    "idealFor": "佛教信徒、雪景摄影爱好者、禅修体验者",
    "introduction": "当纷飞大雪覆盖五台山的琉璃金顶，这座佛教名山化身为纯净无瑕的雪域佛国。落雪无声中，红墙金瓦与晶莹雪色交相辉映，僧侣们踏雪修行的身影更显庄严。本攻略将带您领略雪中五台的极致禅境，从晨钟暮鼓到台顶云海，体验圣洁与空灵的完美交融。",
    "highlights": [
      "朝拜雪中菩萨顶，感受皇家寺庙气度",
      "拍摄显通寺铜殿的金顶积雪奇观",
      "徒步中台顶雪原，邂逅雾凇云海",
      "体验寺院早课与雪中禅修",
      "品尝冬季特供滋补素斋"
    ],
    "itinerary": [
      {
        "title": "雪中初临 & 台怀镇夜韵",
        "description": "抵达冰雪佛国，适应高原气候，初探雪夜古刹",
        "activities": [
          {
            "time": "14:00",
            "title": "五台山门迎雪",
            "description": "乘坐防滑专车进入景区，沿途欣赏雪松雾凇",
            "location": "五台山北门",
            "image": "/images/攻略图片集/五台山攻略/山门雪景2.png"
          },
          {
            "time": "17:30",
            "title": "塔院寺晚课",
            "description": "参加白塔映雪中的暮时诵经",
            "location": "塔院寺",
            "image": "/images/攻略图片集/五台山雪景/五台山雪景3.png"
          }
        ]
      },
      {
        "title": "核心寺庙朝圣",
        "description": "深度探访雪中佛教建筑瑰宝",
        "activities": [
          {
            "time": "06:30",
            "title": "菩萨顶朝霞",
            "description": "拍摄雪中日出时的皇家寺庙群",
            "location": "菩萨顶",
            "image": "/images/攻略图片集/五台山攻略/菩萨顶晨雪.png"
          },
          {
            "time": "10:00",
            "title": "显通寺探秘",
            "description": "观铜殿积雪与无梁殿建筑奇观",
            "location": "显通寺",
            "image": "/images/攻略图片集/五台山攻略/铜殿雪景.png"
          }
        ]
      },
      {
        "title": "台顶雪原徒步",
        "description": "穿越冰雪覆盖的高山草甸",
        "activities": [
          {
            "time": "07:00",
            "title": "中台翠岩峰",
            "description": "乘雪地车登顶，观雪山云海",
            "location": "中台顶",
            "image": "/images/攻略图片集/五台山攻略/台顶云海.png"
          },
          {
            "time": "13:00",
            "title": "澡浴池雪径",
            "description": "徒步朝圣古道，探访雪中圣迹",
            "location": "澡浴池寺"
          }
        ]
      },
      {
        "title": "禅修体验日",
        "description": "参与寺院冬季特别修行活动",
        "activities": [
          {
            "time": "05:30",
            "title": "万佛阁早课",
            "description": "参加雪中晨间诵经仪式",
            "location": "五爷庙"
          },
          {
            "time": "14:00",
            "title": "竹林寺禅茶",
            "description": "体验雪窗茶禅一味",
            "location": "竹林寺",
            "image": "/images/攻略图片集/五台山攻略/禅茶雪景.png"
          }
        ]
      },
      {
        "title": "返程 & 结缘法物",
        "description": "最后朝拜并请购佛教纪念品",
        "activities": [
          {
            "time": "08:00",
            "title": "黛螺顶雪阶",
            "description": "攀登1080级大智路，俯瞰雪中台怀",
            "location": "黛螺顶"
          },
          {
            "time": "12:00",
            "title": "法物流通处",
            "description": "请购六道木佛珠等开光法物",
            "location": "殊像寺"
          }
        ]
      }
    ],
    "tips": [
      {
        "title": "防寒装备",
        "content": "需准备-20℃防寒衣物、冰爪鞋套及防雪墨镜"
      },
      {
        "title": "交通注意",
        "content": "台顶道路可能临时封闭，建议每日确认景区公告"
      },
      {
        "title": "摄影保护",
        "content": "相机电池需贴身保暖，镜头进出室内要密封防雾"
      },
      {
        "title": "礼仪须知",
        "content": "雪中磕头朝拜需备护膝，勿随意清扫寺院积雪"
      }
    ],
    "foodRecommendations": [
      {
        "name": "八功德汤",
        "description": "寺院冬季特供药膳汤，含八种温补食材",
        "image": "/images/攻略图片集/五台山雪景/八功德汤.png",
        "location": "碧山寺斋堂"
      },
      {
        "name": "五台素火锅",
        "description": "台蘑汤底配冻豆腐、山野菜",
        "image": "/images/攻略图片集/五台山雪景/五台素火锅.png",
        "location": "一盏明灯全素斋"
      }
    ],
    "gallery": [
      "/images/攻略图片集/五台山雪景/五台山雪景.png",
      "/images/攻略图片集/五台山雪景/五台山雪景2.png",
      "/images/攻略图片集/五台山雪景/五台山雪景3.png",
      "/images/攻略图片集/五台山雪景/五台山雪景4.png"
    ],
    "author": {
      "name": "雪域行者",
      "avatar": "/images/攻略图片集/五台山攻略/作者雪景.png",
      "bio": "十年冬季五台修行者，著有《五台山雪禅录》"
    }
  }
  // 可以继续添加其他景区数据
]

// 默认攻略数据（如果没有找到匹配的攻略）
const defaultGuide = {
  title: "日本东京五日游完全攻略",
  subtitle: "探索东京的现代与传统之美",
  coverImage: "https://images.unsplash.com/photo-1536098561742-ca998e48cbcc?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1500&q=80",
  duration: "5天4晚",
  budget: "约15000元/人",
  bestTime: "春季 (3月-5月)",
  idealFor: "情侣、家庭",
  introduction: "东京，这座融合了传统与现代的城市，是日本的首都和最大的城市。无论是现代化的摩天大楼，还是古老的寺庙和花园，东京都能给游客带来独特的体验。本攻略将带您领略东京的魅力，从热门景点到隐藏美食，让您的东京之旅丰富多彩。",
  highlights: [
    "参观东京塔和天空树，俯瞰东京全景",
    "在浅草寺感受传统日本文化",
    "探索秋叶原的电子产品和动漫文化",
    "在银座享受购物和美食体验",
    "体验迪士尼乐园的欢乐时光"
  ],
  itinerary: [
    {
      title: "抵达东京 & 浅草寺",
      description: "抵达东京成田机场，办理入住手续后，前往浅草寺参观，感受日本传统文化。",
      activities: [
        {
          time: "14:00",
          title: "抵达成田机场",
          description: "从机场乘坐Skyliner或NEX列车前往市区",
          location: "成田国际机场"
        },
        {
          time: "16:00",
          title: "酒店办理入住",
          description: "放置行李，稍作休息",
          location: "新宿区酒店"
        },
        {
          time: "17:30",
          title: "浅草寺参观",
          description: "参观东京最古老的寺庙，感受传统日本文化",
          image: "https://images.unsplash.com/photo-1583053693270-c196c5aeb6d3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
          location: "浅草寺"
        }
      ]
    },
    {
      title: "东京市区观光",
      description: "参观东京塔、明治神宫等著名景点，感受东京的现代与传统。",
      activities: [
        {
          time: "09:00",
          title: "明治神宫",
          description: "参观东京最重要的神道教神社",
          location: "明治神宫"
        },
        {
          time: "14:00",
          title: "东京塔",
          description: "登上东京塔观景台，俯瞰东京全景",
          location: "东京塔"
        }
      ]
    },
    {
      title: "迪士尼乐园一日游",
      description: "全天游玩东京迪士尼乐园，体验各种精彩的游乐设施和表演。",
      activities: [
        {
          time: "08:00",
          title: "前往迪士尼乐园",
          description: "乘坐地铁前往迪士尼乐园",
          location: "东京迪士尼乐园"
        },
        {
          time: "09:00",
          title: "迪士尼乐园游玩",
          description: "体验各种游乐设施和表演",
          location: "东京迪士尼乐园"
        }
      ]
    }
  ],
  tips: [
    {
      title: "交通",
      content: "东京的公共交通非常发达，建议购买Suica或Pasmo卡，方便乘坐地铁和巴士。"
    },
    {
      title: "住宿",
      content: "新宿、涩谷和东京站附近是住宿的好选择，交通便利，周边设施齐全。"
    },
    {
      title: "餐饮",
      content: "东京的美食种类繁多，从高级寿司到平价拉面应有尽有。不要错过筑地市场的新鲜海鲜。"
    },
    {
      title: "购物",
      content: "银座是高端购物区，而秋叶原则是电子产品和动漫周边的天堂。"
    }
  ],
  foodRecommendations: [
    {
      name: "筑地寿司大",
      description: "提供新鲜的寿司和刺身，是品尝正宗日本寿司的好去处。",
      image: "https://images.unsplash.com/photo-1579871494447-9811cf80d66c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
      location: "筑地市场附近"
    },
    {
      name: "一蘭拉面",
      description: "东京著名的连锁拉面店，以其独特的猪骨汤底和秘制调味料闻名。",
      location: "涩谷区"
    }
  ],
  gallery: [
    "https://images.unsplash.com/photo-1536098561742-ca998e48cbcc?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1500&q=80",
    "https://images.unsplash.com/photo-1583053693270-c196c5aeb6d3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
    "https://images.unsplash.com/photo-1524413840807-0c3cb6fa808d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
    "https://images.unsplash.com/photo-1570521462033-3015e76e7432?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1471&q=80"
  ],
  author: {
    name: "旅行达人",
    avatar: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1160&q=80",
    bio: "资深旅游博主，走过30多个国家，热爱分享旅行经验"
  }
}

// 根据路由参数获取对应的攻略数据
const guide = computed(() => {
  // 获取路由中的name参数
  const name = route.params.name
  
  if (name) {
    // 在攻略数据中查找匹配的攻略
    const foundGuide = guideData.find(g => g.name === name)
    // 如果找到匹配的攻略，返回该攻略，否则返回默认攻略
    return foundGuide || defaultGuide
  }
  
  // 如果没有name参数，返回默认攻略
  return defaultGuide
})

// 组件挂载时的处理
onMounted(() => {
  console.log('当前路由参数:', route.params.name)
})

</script>

<style>
.travel-guide {
  font-family: Arial, sans-serif;
  color: #333;
  max-width: 1200px;
  margin: 0 auto;
}

/* 封面部分 */
.cover-section {
  position: relative;
  height: 500px;
  overflow: hidden;
}

.cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cover-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 30px;
  background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
  color: white;
}

.title {
  font-size: 2.5rem;
  margin: 0 0 10px 0;
}

.subtitle {
  font-size: 1.2rem;
  margin: 0;
  opacity: 0.9;
}

/* 基本信息部分 */
.info-section {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 30px;
  background-color: #f8f8f8;
}

.info-card {
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  padding: 15px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.info-icon {
  margin-right: 15px;
  color: #e53e3e;
}

.info-content {
  display: flex;
  flex-direction: column;
}

.info-label {
  font-size: 0.9rem;
  color: #666;
}

.info-value {
  font-weight: bold;
  margin-top: 5px;
}

/* 主要内容 */
.main-content {
  padding: 30px;
}

.section {
  margin-bottom: 40px;
}

.section-title {
  font-size: 1.8rem;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e53e3e;
  color: #333;
}

/* 行程亮点 */
.highlights-list {
  list-style: none;
  padding: 0;
}

.highlight-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.highlight-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background-color: #e53e3e;
  color: white;
  border-radius: 50%;
  margin-right: 15px;
  font-weight: bold;
}

/* 行程安排 */
.day-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}

.day-tab {
  padding: 8px 16px;
  background-color: #f1f1f1;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.day-tab.active {
  background-color: #e53e3e;
  color: white;
}

.day-title {
  font-size: 1.5rem;
  margin-bottom: 10px;
}

.day-description {
  margin-bottom: 20px;
  color: #555;
}

.activities {
  margin-left: 20px;
}

.activity {
  display: flex;
  margin-bottom: 30px;
  position: relative;
}

.activity:before {
  content: '';
  position: absolute;
  top: 0;
  left: 60px;
  height: 100%;
  width: 2px;
  background-color: #e0e0e0;
  z-index: -1;
}

.activity-time {
  width: 80px;
  font-weight: bold;
  color: #e53e3e;
}

.activity-content {
  flex: 1;
  padding-left: 30px;
  position: relative;
}

.activity-content:before {
  content: '';
  position: absolute;
  left: -5px;
  top: 5px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #e53e3e;
}

.activity-title {
  font-weight: bold;
  margin-bottom: 5px;
}

.activity-description {
  color: #555;
  margin-bottom: 10px;
}

.activity-location {
  display: flex;
  align-items: center;
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 10px;
}

.activity-location svg {
  margin-right: 5px;
  color: #e53e3e;
}

.activity-image {
  width: 100%;
  max-width: 400px;
  border-radius: 8px;
  margin-top: 10px;
}

/* 旅行贴士 */
.tips-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.tip {
  padding: 20px;
  background-color: #f8f8f8;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.tip-title {
  font-size: 1.2rem;
  margin-bottom: 10px;
  color: #e53e3e;
}

.tip-content {
  color: #555;
}

/* 美食推荐 */
.food-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.food-item {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.food-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
}

.food-content {
  padding: 15px;
}

.food-name {
  font-size: 1.2rem;
  margin-bottom: 10px;
}

.food-description {
  color: #555;
  margin-bottom: 10px;
}

.food-location {
  display: flex;
  align-items: center;
  color: #666;
  font-size: 0.9rem;
}

.food-location svg {
  margin-right: 5px;
  color: #e53e3e;
}

/* 图片画廊 */
.gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
}

.gallery-item {
  border-radius: 8px;
  overflow: hidden;
  height: 200px;
}

.gallery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.gallery-image:hover {
  transform: scale(1.05);
}

/* 作者信息 */
.author-section {
  border-top: 1px solid #e0e0e0;
  padding-top: 30px;
}

.author-info {
  display: flex;
  align-items: center;
}

.author-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 20px;
}

.author-name {
  font-size: 1.3rem;
  margin: 0 0 5px 0;
}

.author-bio {
  color: #555;
  margin: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .cover-section {
    height: 300px;
  }
  
  .title {
    font-size: 1.8rem;
  }
  
  .subtitle {
    font-size: 1rem;
  }
  
  .info-section {
    flex-direction: column;
  }
  
  .info-card {
    width: 100%;
  }
  
  .tips-container,
  .food-container,
  .gallery {
    grid-template-columns: 1fr;
  }
}
</style>